import { z } from 'zod'

import { userSchema } from './user'
import { managementUserSchema } from './managementUser'
import { AuthType } from '../constants/auth'
import { dbEntitySchema } from './system'

export const authSchema = dbEntitySchema.extend({
  token: z.string(),
  managementUser: z.string(),
  user: z.string(),
})

export const authSchemaWithUser = authSchema.extend({
  user: userSchema,
})

export const authSchemaWithManagementUser = authSchema.extend({
  managementUser: managementUserSchema,
})

//requests
export const authCreateArgs = z.object({
  email: z.string(),
  password: z.string(),
  type: z.nativeEnum(AuthType),
})

export const authForgotPasswordArgs = z.object({
  email: z.string().email(),
  type: z.nativeEnum(AuthType),
})

export const authResetPasswordArgs = z
  .object({
    password: z.string(),
    email: z.string().email().optional(),
    resetCode: z.string().optional(),
    resetToken: z.string().optional(),
    emailVerificationToken: z.string().optional(),
    type: z.nativeEnum(AuthType),
  })
  .superRefine((data, ctx) => {
    const hasResetToken = Boolean(data.resetToken)
    const hasResetCodeFlow = Boolean(data.email && data.resetCode)

    if (!hasResetToken && !hasResetCodeFlow) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Either resetToken or email + resetCode is required',
        path: ['resetToken'],
      })
    }
  })

export const authSetManagementPasswordArgs = z.object({
  password: z.string(),
  managementUserId: z.string(),
  type: z.nativeEnum(AuthType),
})

export const consumerAuthRegisterBodySchema = z.object({
  email: z.string().email(),
  password: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
})

export const consumerAuthLoginBodySchema = z.object({
  email: z.string().email(),
  password: z.string(),
})

export const consumerAuthGoogleBodySchema = z.object({
  idToken: z.string().min(1),
  intent: z.enum(['signIn', 'signUp']),
  client: z.string().optional(),
})

export const consumerAuthGoogleConnectBodySchema = z.object({
  idToken: z.string().min(1),
})

export const consumerAuthSetPasswordBodySchema = z.object({
  password: z.string().min(1),
})

export const consumerAuthForgotBodySchema = z.object({
  email: z.string().email(),
})

export const consumerAuthLoginCodeRequestBodySchema = z.object({
  email: z.string().email(),
})

export const consumerAuthLoginCodeVerifyBodySchema = z.object({
  email: z.string().email(),
  code: z.string().min(1),
})

export const managementAuthLoginCodeRequestBodySchema = z.object({
  email: z.string().email(),
})

export const managementAuthLoginCodeVerifyBodySchema = z.object({
  email: z.string().email(),
  code: z.string().min(1),
})

export const managementAuthGoogleBodySchema = z.object({
  idToken: z.string().min(1),
})

export const consumerAuthResetBodySchema = z.object({
  email: z.string().email(),
  resetCode: z.string().min(1),
  password: z.string(),
})

export const consumerAuthVerifyBodySchema = z.object({
  code: z.string().min(1),
})

export const consumerAuthGoogleLinkingSchema = z.object({
  googleEmail: z.string().email().nullish(),
  accountEmail: z.string().email().nullish(),
  emailMismatch: z.boolean(),
})

export const consumerAuthResponseSchema = z.object({
  token: z.string(),
  user: userSchema,
  googleLinking: consumerAuthGoogleLinkingSchema.optional(),
})

export const consumerAuthMessageResponseSchema = z.object({
  message: z.string(),
})

export const consumerAuthSuccessResponseSchema = z.object({
  success: z.literal(true),
})

export const managementAuthLoginCodeVerifyResponseSchema = z.object({
  token: z.string(),
})

export const managementAuthGoogleResponseSchema = z.object({
  token: z.string(),
})
